//TODO : Refactor after ai
/**
 * Modul 5 – Disposition: Plantafel über zwei Wochen × Vormittag/Nachmittag je Gruppenführer,
 * native Drag & Drop aus dem Auftragsvorrat, Abwesenheiten blockieren Zellen, Monatsleiste 9 Monate.
 * Annahmen: ANNAHMEN C6, D1.
 */
import { Fragment, useMemo, useState, type DragEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useDatenStore } from "@app/store";
import { datensaetze, type Datensatz } from "@shared/demo-data/viacid";
import { ch, datumCH, ein, str, viele, ziel, neueId } from "./lib";
import { Karte, Kennzahl, Knopf, Pille, Seite } from "./ui";
import { AnlegenKnopf } from "./ActionLeiste";
import { objektKlasse as klasseVon } from "@shared/demo-data/viacid";

const SLOTS = ["Vormittag", "Nachmittag"] as const;
const WOCHENTAGE = ["Mo", "Di", "Mi", "Do", "Fr"];

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function montag(ref: Date): Date {
  const d = new Date(ref);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  d.setHours(0, 0, 0, 0);
  return d;
}

export function PlanungPage() {
  const b = useDatenStore((s) => s.bestand);
  const anlegen = useDatenStore((s) => s.anlegen);
  const aendern = useDatenStore((s) => s.aendern);
  const nav = useNavigate();
  const [start, setStart] = useState(() => montag(new Date()));
  const [over, setOver] = useState<string | null>(null);

  const tage = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => {
        const d = new Date(start);
        d.setDate(d.getDate() + i + (i >= 5 ? 2 : 0));
        return d;
      }),
    [start]
  );
  const heuteIso = iso(new Date());
  const naechsteWocheEnde = iso(new Date(start.getTime() + 7 * 86400000));

  const mitarbeiter = datensaetze(b, "mitarbeiter");
  const zeilen = mitarbeiter.filter((m) =>
    /gruppenf|unterhalt|maschin|produktion/i.test(str(m.werte.funktion))
  );
  const einsaetze = datensaetze(b, "einsatz");
  const abwesenheiten = datensaetze(b, "abwesenheit");
  const fahrzeuge = datensaetze(b, "fahrzeug");

  const vorrat = datensaetze(b, "auftrag").filter(
    (a) =>
      a.werte.auftragsart === "Dienstleistung" &&
      ["Erteilt", "Freigegeben"].includes(a.status ?? "") &&
      !einsaetze.some(
        (e) =>
          ein(e.bezuege["einsatz-fuer-auftrag"]) === a.id &&
          !["Ausgeführt", "Ausführung abgesagt"].includes(e.status ?? "")
      )
  );

  const zelle = (gf: Datensatz, tag: Date, slot: string) => {
    const t = iso(tag);
    const eigene = einsaetze.filter(
      (e) =>
        str(e.werte.einsatzdatum) === t &&
        str(e.werte.zeitslot) === slot &&
        (ein(e.bezuege["einsatz-gefuehrt-von-mitarbeiter"]) === gf.id ||
          viele(e.bezuege["einsatz-team"]).includes(gf.id))
    );
    const abw = abwesenheiten.find(
      (a) =>
        ein(a.bezuege["abwesenheit-betrifft-mitarbeiter"]) === gf.id &&
        str(a.werte.von) <= t &&
        str(a.werte.bis) >= t
    );
    return { eigene, abw };
  };

  const onDrop =
    (gf: Datensatz, tag: Date, slot: string) => (ev: DragEvent) => {
      ev.preventDefault();
      setOver(null);
      const daten = ev.dataTransfer.getData("text/plain");
      const [art, id] = daten.split(":");
      const t = iso(tag);
      if (zelle(gf, tag, slot).abw) return;
      const fix = t <= naechsteWocheEnde;
      if (art === "auftrag") {
        const au = datensaetze(b, "auftrag").find((x) => x.id === id);
        if (!au) return;
        const team = mitarbeiter
          .filter((m) => /unterhalt/i.test(str(m.werte.funktion)))
          .slice(0, 2)
          .map((m) => m.id);
        const fz = fahrzeuge.find((f) =>
          /spritz|spezial/i.test(
            str(f.werte.fahrzeugart) + str(f.werte.bezeichnung)
          )
        );
        anlegen(
          "einsatz",
          {
            id: neueId("e"),
            titel: `${slot} · ${au.titel}`,
            untertitel: `${t} · ${slot}`,
            kennung: null,
            status: fix ? "Terminiert" : "Geplant",
            werte: {
              einsatzdatum: t,
              zeitslot: slot,
              einsatzhinweise: `Aus Auftrag ${au.kennung} disponiert`,
            },
            bezuege: {
              "einsatz-fuer-auftrag": au.id,
              "einsatz-gefuehrt-von-mitarbeiter": gf.id,
              "einsatz-team": [gf.id, ...team],
              ...(fz ? { "einsatz-nutzt-fahrzeug": [fz.id] } : {}),
              ...(ein(au.bezuege["auftrag-betrifft-strassenobjekt"])
                ? {
                    "einsatz-am-strassenobjekt": ein(
                      au.bezuege["auftrag-betrifft-strassenobjekt"]
                    ) as string,
                  }
                : {}),
            },
          },
          `${au.kennung} am ${datumCH(t)} ${slot} eingeplant (${fix ? "fix" : "provisorisch"}) – Termin im Kalender.`
        );
      } else if (art === "einsatz") {
        aendern(
          {
            slug: "einsatz",
            id,
            status: fix ? "Terminiert" : "Geplant",
            werte: { einsatzdatum: t, zeitslot: slot },
            bezuege: { "einsatz-gefuehrt-von-mitarbeiter": gf.id },
          },
          `Einsatz verschoben auf ${datumCH(t)} ${slot}.`
        );
      }
    };

  // Monatsleiste (9 Monate)
  const monate = Array.from({ length: 9 }, (_, i) => {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return {
      key,
      label: d.toLocaleDateString("de-CH", { month: "short", year: "2-digit" }),
      anzahl: einsaetze.filter((e) => str(e.werte.einsatzdatum).startsWith(key))
        .length,
    };
  });
  const kapazitaet = zeilen.length * 2 * 21;

  return (
    <Seite
      titel="Disposition"
      untertitel="Nächste Woche fix, übernächste provisorisch · Halbtages-Slots · Abwesenheiten sperren Zellen"
      aktionen={
        <>
          <Knopf
            onClick={() =>
              setStart((s) => new Date(s.getTime() - 7 * 86400000))
            }
          >
            ◀ Woche
          </Knopf>
          <Knopf onClick={() => setStart(montag(new Date()))}>Heute</Knopf>
          <Knopf
            onClick={() =>
              setStart((s) => new Date(s.getTime() + 7 * 86400000))
            }
          >
            Woche ▶
          </Knopf>
          {klasseVon("abwesenheit") && (
            <AnlegenKnopf klasse={klasseVon("abwesenheit")!} />
          )}
        </>
      }
    >
      <div className="vc-grid c4" style={{ marginBottom: 16 }}>
        <Kennzahl
          label="Aufträge im Vorrat"
          wert={vorrat.length}
          ton={vorrat.length ? "warn" : undefined}
          hint="noch ohne Einsatz"
        />
        <Kennzahl
          label="Einsätze diese 2 Wochen"
          wert={
            einsaetze.filter((e) =>
              tage.some((t) => iso(t) === str(e.werte.einsatzdatum))
            ).length
          }
        />
        <Kennzahl
          label="Teams"
          wert={zeilen.length}
          hint="Kolonne (Gruppenführer und Mitarbeitende)"
        />
        <Kennzahl label="Fahrzeuge" wert={fahrzeuge.length} />
      </div>

      <div className="vc-grid main-side">
        <Karte
          titel="Plantafel"
          sub={`${datumCH(iso(tage[0]))} – ${datumCH(iso(tage[9]))}`}
        >
          <div style={{ overflowX: "auto" }}>
            <div
              className="vc-board"
              style={{
                gridTemplateColumns: `140px repeat(20, minmax(92px, 1fr))`,
                minWidth: 1100,
              }}
            >
              <div className="hd">Team</div>
              {tage.map((t) =>
                SLOTS.map((s, si) => (
                  <div
                    key={iso(t) + s}
                    className={`hd ${iso(t) === heuteIso ? "" : ""}`}
                    style={{
                      borderLeft: si === 0 ? "2px solid #d6d9d8" : undefined,
                      background:
                        iso(t) > naechsteWocheEnde ? "#f7f3ee" : undefined,
                    }}
                  >
                    {si === 0
                      ? `${WOCHENTAGE[(t.getDay() + 6) % 7]} ${t.getDate()}.${t.getMonth() + 1}.`
                      : ""}
                    <small
                      style={{
                        display: "block",
                        fontWeight: 400,
                        color: "var(--vc-muted)",
                      }}
                    >
                      {s}
                      {iso(t) > naechsteWocheEnde && si === 1 ? " · prov." : ""}
                    </small>
                  </div>
                ))
              )}
              {zeilen.map((gf) => (
                <Fragment key={gf.id}>
                  <div className="hd" style={{ minHeight: 64 }}>
                    {gf.titel}
                    <small style={{ display: "block", fontWeight: 400 }}>
                      {ch(str(gf.werte.funktion))}
                    </small>
                  </div>
                  {tage.map((t) =>
                    SLOTS.map((s, si) => {
                      const z = zelle(gf, t, s);
                      const key = `${gf.id}|${iso(t)}|${s}`;
                      return (
                        <div
                          key={key}
                          className={`cell ${z.abw ? "blocked" : ""} ${over === key ? "over" : ""}`}
                          style={{
                            borderLeft:
                              si === 0 ? "2px solid #d6d9d8" : undefined,
                          }}
                          onDragOver={(e) => {
                            if (!z.abw) {
                              e.preventDefault();
                              setOver(key);
                            }
                          }}
                          onDragLeave={() => setOver(null)}
                          onDrop={onDrop(gf, t, s)}
                          title={
                            z.abw
                              ? `${str(z.abw.werte.abwesenheitsart)} ${datumCH(z.abw.werte.von)}–${datumCH(z.abw.werte.bis)}`
                              : undefined
                          }
                        >
                          {z.abw && (
                            <small style={{ color: "var(--vc-muted)" }}>
                              {str(z.abw.werte.abwesenheitsart)}
                            </small>
                          )}
                          {z.eigene.map((e) => {
                            const au = ziel(
                              b,
                              e,
                              "einsatz-fuer-auftrag",
                              "auftrag"
                            );
                            const fz = viele(
                              e.bezuege["einsatz-nutzt-fahrzeug"]
                            )
                              .map(
                                (id) =>
                                  fahrzeuge.find((f) => f.id === id)?.kennung
                              )
                              .filter(Boolean)
                              .join(", ");
                            const fertig = [
                              "Ausgeführt",
                              "Ausführung abgesagt",
                            ].includes(e.status ?? "");
                            return (
                              <div
                                key={e.id}
                                className={`chip ${fertig ? "done" : ""} ${e.status === "Geplant" ? "prov" : ""} ${e.status === "Ausführung abgesagt" ? "abgesagt" : ""}`}
                                title={
                                  e.status === "Ausführung abgesagt"
                                    ? "Ausführung abgesagt"
                                    : undefined
                                }
                                draggable={!fertig}
                                onDragStart={(ev) =>
                                  ev.dataTransfer.setData(
                                    "text/plain",
                                    `einsatz:${e.id}`
                                  )
                                }
                                onClick={() => nav(`/app/baustelle/${e.id}`)}
                              >
                                {au?.titel ?? e.titel}
                                <small>
                                  {
                                    ziel(b, au, "auftrag-von-kunde", "kunde")
                                      ?.titel
                                  }
                                  {fz ? ` · ${fz}` : ""}
                                </small>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        </Karte>

        <div className="vc-grid">
          <Karte
            titel="Auftragsvorrat"
            sub="Auf einen Halbtag ziehen"
            rechts={<Pille farbe="orange">{vorrat.length}</Pille>}
          >
            <div className="vc-pool">
              {vorrat.map((a) => (
                <div
                  key={a.id}
                  className="chip"
                  draggable
                  onDragStart={(ev) =>
                    ev.dataTransfer.setData("text/plain", `auftrag:${a.id}`)
                  }
                >
                  <strong>{a.kennung}</strong> · {a.titel}
                  <div className="sub">
                    {ziel(b, a, "auftrag-von-kunde", "kunde")?.titel} · Wunsch{" "}
                    {datumCH(a.werte.ausfuehrungszeitraumVon)} –{" "}
                    {datumCH(a.werte.ausfuehrungszeitraumBis)}
                  </div>
                </div>
              ))}
              {vorrat.length === 0 && (
                <div className="sub">Alle Aufträge sind disponiert.</div>
              )}
            </div>
          </Karte>
          <Karte
            titel="Langfristsicht"
            sub="Einsätze je Monat · Kapazität aus Teams × Halbtagen"
          >
            <div
              style={{
                display: "flex",
                gap: 6,
                alignItems: "flex-end",
                height: 110,
              }}
            >
              {monate.map((m) => (
                <div
                  key={m.key}
                  style={{ flex: 1, textAlign: "center", fontSize: "0.7rem" }}
                >
                  <div
                    style={{
                      height: Math.max(
                        4,
                        (90 * m.anzahl) / Math.max(1, kapazitaet / 10)
                      ),
                      background: m.anzahl
                        ? "var(--vc-orange)"
                        : "var(--vc-line)",
                      borderRadius: 4,
                    }}
                    title={`${m.anzahl} Einsätze`}
                  />
                  <div style={{ marginTop: 4, color: "var(--vc-muted)" }}>
                    {m.label}
                  </div>
                </div>
              ))}
            </div>
          </Karte>
          <Karte titel="Abwesenheiten" sub="Ferien, Krankheit, Kurse">
            <div className="vc-list">
              {abwesenheiten.map((a) => (
                <div key={a.id} className="vc-row">
                  <div className="grow">
                    <div className="title">
                      {
                        ziel(
                          b,
                          a,
                          "abwesenheit-betrifft-mitarbeiter",
                          "mitarbeiter"
                        )?.titel
                      }
                    </div>
                    <div className="meta">
                      {str(a.werte.abwesenheitsart)} · {datumCH(a.werte.von)} –{" "}
                      {datumCH(a.werte.bis)}
                    </div>
                  </div>
                  <Pille status={a.status} />
                </div>
              ))}
            </div>
          </Karte>
        </div>
      </div>
      <p className="sub" style={{ marginTop: 10 }}>
        Outlook-Abgleich und Fahrzeugkonflikte sind im Prototyp noch nicht
        abgebildet (offene Punkte).
      </p>
    </Seite>
  );
}
